// src/components/LangSwitcher.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LangSwitcher = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Idioma actual por slash
  const currentLang = location.pathname.startsWith("/en") ? "en" : "es";

  // Etiquetas
  const labels = useMemo(() => ({
    es: "Español",
    en: "English",
  }), []);

  // Mapea ruta actual a su contraparte en el otro idioma
  const mapPathToLang = (lang) => {
    const path = location.pathname;
    const search = location.search || "";
    const hash = location.hash || "";

    // Rutas previstas
    if (lang === "en") {
      if (path.startsWith("/es/articulos")) return "/en/articles" + search + hash;
      if (path.startsWith("/es/confirmacion")) return "/en/confirmation" + search + hash;
      if (path.startsWith("/es")) return "/en" + search + hash;
      // fallback
      return "/en" + search + hash;
    } else {
      if (path.startsWith("/en/articles")) return "/es/articulos" + search + hash;
      if (path.startsWith("/en/confirmation")) return "/es/confirmacion" + search + hash;
      if (path.startsWith("/en")) return "/es" + search + hash;
      // fallback
      return "/es" + search + hash;
    }
  };

  const changeLang = (lang) => {
    setOpen(false);
    if (lang !== currentLang) {
      navigate(mapPathToLang(lang), { replace: false });
    }
  };

  // Cerrar al hacer click fuera
  useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Accesibilidad teclado
  const onKeyDown = (e) => {
    if (e.key === "Escape") setOpen(false);
  };

  // Estilos base
  const btnStyle = {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid #2a2a2a",
    color: "#fff",
    padding: ".45rem .6rem",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 6,
    userSelect: "none",
  };

  const caretStyle = { opacity: 0.8 };

  const menuStyle = {
    position: "absolute",
    right: 0,
    top: "calc(100% + 6px)",
    background: "#111",
    border: "1px solid #2a2a2a",
    borderRadius: 10,
    boxShadow: "0 14px 30px rgba(0,0,0,.45)",
    padding: 6,
    zIndex: 9999,
    minWidth: 140,
  };

  const itemStyle = (active) => ({
    width: "100%",
    textAlign: "left",
    background: active ? "rgba(233,63,99,.15)" : "transparent",
    border: active ? "1px solid rgba(233,63,99,.35)" : "1px solid transparent",
    color: "#fff",
    padding: ".5rem .65rem",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  });

  return (
    <div style={{ position: "relative" }} onKeyDown={onKeyDown}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={btnStyle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language selector"
      >
        {labels[currentLang]} <span style={caretStyle}>▾</span>
      </button>

      {open && (
        <div ref={menuRef} role="listbox" style={menuStyle}>
          <button
            role="option"
            aria-selected={currentLang === "es"}
            style={itemStyle(currentLang === "es")}
            onClick={() => changeLang("es")}
          >
            Español
          </button>
          <button
            role="option"
            aria-selected={currentLang === "en"}
            style={{ ...itemStyle(currentLang === "en"), marginTop: 4 }}
            onClick={() => changeLang("en")}
          >
            English
          </button>
        </div>
      )}
    </div>
  );
};

export default LangSwitcher;
